import { useState } from 'react';
import type { PostDto, VersionDto } from '@str/api/types';
import { instagramUsernameSchema, type CardTag } from '@str/shared';
import { unwrap } from '@/lib/api';
import './composer.css';

type Location = { id: string; name: string; address?: string };
type Props = {
  post: PostDto;
  version: VersionDto;
  connectionId?: string;
  onChange: (post: PostDto) => void;
  disabled?: boolean;
};

/** A new version remounts the editor; local unsaved details belong to that exact picture set. */
export function PostDetails(props: Props) {
  const [open, setOpen] = useState(false);
  const [savedVersionId, setSavedVersionId] = useState<string | null>(null);
  return (
    <section className="post-details">
      <button
        type="button"
        className="post-details-toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        Details <span aria-hidden="true">{open ? '−' : '+'}</span>
      </button>
      <div hidden={!open}>
        <DetailsFields
          key={`${props.version.id}:${props.post.format}`}
          {...props}
          onSaved={setSavedVersionId}
        />
        {savedVersionId === props.version.id && <p role="status">Details saved</p>}
      </div>
    </section>
  );
}

function DetailsFields({
  post,
  version,
  connectionId,
  onChange,
  disabled,
  onSaved,
}: Props & { onSaved: (versionId: string | null) => void }) {
  const [index, setIndex] = useState(0);
  const [cards, setCards] = useState(() =>
    version.cards.map((c) => ({ altText: c.altText, tags: c.tags ?? [] })),
  );
  const [username, setUsername] = useState('');
  const [point, setPoint] = useState({ x: 0.5, y: 0.5 });
  const [collaborator, setCollaborator] = useState('');
  const [collaborators, setCollaborators] = useState(post.collaborators ?? []);
  const [location, setLocation] = useState<Location | null>(post.location ?? null);
  const [query, setQuery] = useState('');
  const [locations, setLocations] = useState<Location[]>([]);
  const [searching, setSearching] = useState(false);
  const [mock, setMock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const isStory = post.format === 'Story';
  const isVideo = !!version.videoUrl || post.format === 'Reel';
  const altAllowed = post.format === 'Image' || post.format === 'Carousel';
  const instagramDisabled = !connectionId || disabled || busy;
  const card = cards[index];
  const changeCard = (patch: Partial<{ altText: string; tags: CardTag[] }>) => {
    setCards(cards.map((c, i) => (i === index ? { ...c, ...patch } : c)));
    onSaved(null);
  };
  const validUsername = (input: string) => {
    const parsed = instagramUsernameSchema.safeParse(input.trim().replace(/^@/, ''));
    if (!parsed.success) {
      setError('Use a public Instagram username with letters, numbers, dots or underscores.');
      return null;
    }
    setError('');
    return parsed.data;
  };
  const search = async () => {
    if (instagramDisabled || query.trim().length < 2) return;
    setSearching(true);
    setError('');
    try {
      const params = new URLSearchParams({
        q: query.trim(),
        propertyId: post.propertyId,
        connectionId,
      });
      const res = await unwrap<{ locations: Location[]; mode: string }>(
        fetch(`/api/channels/instagram/locations?${params}`, { credentials: 'include' }),
      );
      setLocations(res.locations);
      setMock(res.mode === 'mock');
      if (!res.locations.length) setError('No physical locations found. Try a nearby place name.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not search locations');
    } finally {
      setSearching(false);
    }
  };
  const save = async () => {
    setBusy(true);
    setError('');
    onSaved(null);
    let updated = post;
    try {
      const patches = cards.flatMap((c, i) => {
        const original = version.cards[i]!;
        const altChanged = altAllowed && c.altText !== original.altText;
        const tagsChanged =
          !!connectionId && JSON.stringify(c.tags) !== JSON.stringify(original.tags ?? []);
        return altChanged || tagsChanged
          ? [
              {
                index: i,
                imageUrl: original.imageUrl,
                ...(altChanged ? { altText: c.altText } : {}),
                ...(tagsChanged ? { tags: c.tags } : {}),
              },
            ]
          : [];
      });
      const locationChanged =
        !isStory && JSON.stringify(location) !== JSON.stringify(post.location ?? null);
      const collaboratorsChanged =
        !isStory && JSON.stringify(collaborators) !== JSON.stringify(post.collaborators ?? []);
      if (locationChanged || collaboratorsChanged)
        updated = (
          await unwrap<{ post: PostDto }>(
            fetch(`/api/posts/${post.id}`, {
              method: 'PATCH',
              credentials: 'include',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({
                ...(locationChanged
                  ? { location: location ? { id: location.id, name: location.name } : null }
                  : {}),
                ...(collaboratorsChanged ? { collaborators } : {}),
              }),
            }),
          )
        ).post;
      if (patches.length)
        updated = (
          await unwrap<{ post: PostDto }>(
            fetch(`/api/posts/${post.id}/versions/details`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ parentVersionId: version.id, cards: patches }),
            }),
          )
        ).post;
      onChange(updated);
      onSaved(updated.currentVersionId ?? version.id);
    } catch (e) {
      if (updated !== post) onChange(updated);
      setError(e instanceof Error ? e.message : 'Could not save details');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="post-details-fields">
      {!connectionId && (
        <p className="post-details-hint">
          Connect Instagram in Channels to add people, collaborators and a location.
        </p>
      )}
      {post.format === 'Carousel' && (
        <label>
          Card
          <select
            aria-label="Details card"
            value={index}
            onChange={(e) => {
              setIndex(Number(e.target.value));
              setPoint({ x: 0.5, y: 0.5 });
            }}
          >
            {cards.map((_, i) => (
              <option key={i} value={i}>
                Card {i + 1}
              </option>
            ))}
          </select>
        </label>
      )}
      {altAllowed && card && (
        <label>
          Alt text
          <textarea
            aria-label="Alt text"
            maxLength={1000}
            value={card.altText}
            disabled={disabled || busy}
            onChange={(e) => changeCard({ altText: e.target.value })}
          />
          <span className="post-details-hint">
            Describe the picture for people using screen readers. {card.altText.length}/1000
          </span>
        </label>
      )}
      {card && (
        <div>
          <span className="label-caps">Tag people</span>
          {!isVideo && version.cards[index]?.imageUrl && (
            <button
              type="button"
              className="post-details-picture"
              aria-label="Choose tag position"
              disabled={instagramDisabled}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setPoint(
                  e.detail === 0
                    ? { x: 0.5, y: 0.5 }
                    : {
                        x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
                        y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
                      },
                );
              }}
            >
              <img src={version.cards[index].imageUrl} alt="Choose where to tag a person" />
              <span
                className="post-details-pin"
                style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
              >
                +
              </span>
              {card.tags.map((tag, i) => (
                <span
                  key={i}
                  className="post-details-pin"
                  style={{ left: `${tag.x * 100}%`, top: `${tag.y * 100}%` }}
                >
                  •
                </span>
              ))}
            </button>
          )}
          <p className="post-details-hint">
            {isVideo
              ? 'Tag a public Instagram account.'
              : 'Tap the picture to place a tag, then enter a public Instagram username.'}
          </p>
          <div className="post-details-row">
            <input
              aria-label="Instagram username"
              placeholder="@username"
              value={username}
              disabled={instagramDisabled || card.tags.length >= 20}
              onChange={(e) => setUsername(e.target.value)}
            />
            <button
              type="button"
              disabled={instagramDisabled || card.tags.length >= 20 || !username.trim()}
              onClick={() => {
                const name = validUsername(username);
                if (name && !card.tags.some((t) => t.username === name)) {
                  changeCard({ tags: [...card.tags, { username: name, ...point }] });
                  setUsername('');
                }
              }}
            >
              Add person
            </button>
          </div>
          <div className="post-details-chips">
            {card.tags.map((tag, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Remove @${tag.username}`}
                disabled={instagramDisabled}
                onClick={() => changeCard({ tags: card.tags.filter((_, n) => n !== i) })}
              >
                @{tag.username} ×
              </button>
            ))}
          </div>
        </div>
      )}
      {!isStory && (
        <>
          <div>
            <span className="label-caps">Instagram co-authors · optional</span>
            <p className="post-details-hint">
              Invite another Instagram account to publish this post together. Leave empty for your
              own account.
            </p>
            <div className="post-details-row">
              <input
                aria-label="Collaborator username"
                placeholder="@username"
                value={collaborator}
                disabled={instagramDisabled || collaborators.length >= 3}
                onChange={(e) => setCollaborator(e.target.value)}
              />
              <button
                type="button"
                disabled={instagramDisabled || collaborators.length >= 3 || !collaborator.trim()}
                onClick={() => {
                  const name = validUsername(collaborator);
                  if (name && !collaborators.includes(name)) {
                    setCollaborators([...collaborators, name]);
                    setCollaborator('');
                    onSaved(null);
                  }
                }}
              >
                Add collaborator
              </button>
            </div>
            <div className="post-details-chips">
              {collaborators.map((name) => (
                <button
                  key={name}
                  type="button"
                  aria-label={`Remove collaborator @${name}`}
                  disabled={instagramDisabled}
                  onClick={() => {
                    setCollaborators(collaborators.filter((c) => c !== name));
                    onSaved(null);
                  }}
                >
                  @{name} ×
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="label-caps">Location</span>
            {location && (
              <div className="post-details-chips">
                <button
                  type="button"
                  aria-label="Remove location"
                  disabled={instagramDisabled}
                  onClick={() => {
                    setLocation(null);
                    onSaved(null);
                  }}
                >
                  {location.name} ×
                </button>
              </div>
            )}
            <div className="post-details-row">
              <input
                aria-label="Search locations"
                placeholder="Search for a place"
                maxLength={100}
                value={query}
                disabled={instagramDisabled}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void search();
                  }
                }}
              />
              <button
                type="button"
                disabled={instagramDisabled || searching || query.trim().length < 2}
                onClick={() => void search()}
              >
                {searching ? 'Searching…' : 'Search'}
              </button>
            </div>
            {mock && <p className="post-details-hint">Fixture locations for local testing.</p>}
            <ul className="post-details-locations">
              {locations.map((place) => (
                <li key={place.id}>
                  <button
                    type="button"
                    disabled={instagramDisabled}
                    onClick={() => {
                      setLocation({ id: place.id, name: place.name });
                      setLocations([]);
                      onSaved(null);
                    }}
                  >
                    {place.name}
                    {place.address && <small>{place.address}</small>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
      {error && <p role="alert">{error}</p>}
      <button
        type="button"
        className="btn primary"
        disabled={disabled || busy}
        onClick={() => void save()}
      >
        {busy ? 'Saving…' : 'Save details'}
      </button>
    </div>
  );
}
